import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import TransferActivityCardPDF from "./TransferActivityCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { TransferActivityResponseDto } from "@packages/api/madlenAPI.schemas";
import { BookMinusIcon, RefreshCw } from "lucide-react";
import Text from "@core/text";
import { Button } from "@core/button";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetTransferActivityQueryKey,
  getGetUserTransferActivitiesQueryKey,
  updateTransferActivity,
} from "@packages/api/madlenAPI";

interface TransferActivityCardProps {
  transferActivity: TransferActivityResponseDto;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}

export const TransferActivityCard = ({
  transferActivity,
  isImageLoading,
  onCreateAgain,
  isEditable = false,
}: TransferActivityCardProps) => {
  const finalImageUrl = useImagePdf(transferActivity.imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();
  const contentLanguage = transferActivity.contentLanguage || "tr";

  return (
    <div className="space-y-6">
      <CardImage
        createdAt={transferActivity.createdAt}
        imageUrl={transferActivity.imageUrl}
        videoUrl={transferActivity.videoUrl}
        courseName={transferActivity.courseName}
        gradeLevel={Number(transferActivity.gradeLevel)}
        theme="terra"
        isLoading={isImageLoading}
        contentLanguage={contentLanguage}
        topic={
          <div className="flex items-center gap-4 justify-center">
            <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
              <BookMinusIcon size={24} />
            </div>
            <div>
              <Text variant={"h1"} className="mb-2">
                {transferActivity.title}
              </Text>
            </div>
          </div>
        }
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-[#D98787] to-[#D98787]/90"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={transferActivity.title ?? ""}
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          document={
            <TransferActivityCardPDF
              customizationSettings={customizationSettings}
              transferActivity={{
                ...transferActivity,
                imageUrl: finalImageUrl,
              }}
            />
          }
          btnTheme="bg-gradient-to-r from-[#D98787] to-[#D98787]/90"
        />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 space-y-6">
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {tC(contentLanguage, "transferActivities.description")}
          </h3>
          <EditableMarkdown
            value={transferActivity.description ?? ""}
            isEditable={isEditable ?? true}
            handleSave={async (newValue) => {
              await updateTransferActivity(transferActivity.id, {
                description: newValue,
              });

              await queryClient.invalidateQueries({
                queryKey: getGetUserTransferActivitiesQueryKey(),
              });
              await queryClient.invalidateQueries({
                queryKey: getGetTransferActivityQueryKey(transferActivity.id),
              });
            }}
          />
        </div>

        <div className="flex flex-col gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {tC(contentLanguage, "transferActivities.realLifeConnection")}
            </h3>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <EditableMarkdown
                value={transferActivity.realLifeConnection ?? ""}
                isEditable={isEditable ?? true}
                handleSave={async (newValue) => {
                  await updateTransferActivity(transferActivity.id, {
                    realLifeConnection: newValue,
                  });

                  await queryClient.invalidateQueries({
                    queryKey: getGetUserTransferActivitiesQueryKey(),
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetTransferActivityQueryKey(
                      transferActivity.id,
                    ),
                  });
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {tC(contentLanguage, "transferActivities.studentTask")}
            </h3>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
              <EditableMarkdown
                value={transferActivity.studentTask ?? ""}
                isEditable={isEditable ?? true}
                handleSave={async (newValue) => {
                  await updateTransferActivity(transferActivity.id, {
                    studentTask: newValue,
                  });

                  await queryClient.invalidateQueries({
                    queryKey: getGetUserTransferActivitiesQueryKey(),
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetTransferActivityQueryKey(
                      transferActivity.id,
                    ),
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {tC(contentLanguage, "transferActivities.teacherRole")}
          </h3>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
            <EditableMarkdown
              value={transferActivity.teacherRole ?? ""}
              isEditable={isEditable ?? true}
              handleSave={async (newValue) => {
                await updateTransferActivity(transferActivity.id, {
                  teacherRole: newValue,
                });

                await queryClient.invalidateQueries({
                  queryKey: getGetUserTransferActivitiesQueryKey(),
                });
                await queryClient.invalidateQueries({
                  queryKey: getGetTransferActivityQueryKey(transferActivity.id),
                });
              }}
            />
          </div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <TransferActivityCardPDF
            customizationSettings={customizationSettings}
            transferActivity={{
              ...transferActivity,
              imageUrl: finalImageUrl,
            }}
          />
        }
      />
    </div>
  );
};
